<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useUserStore } from '../../stores/userStore'
import { useProfileStore } from '../../stores/profileStore'
import { useNotificationStore } from '../../stores/notificationStore'
import AvatarCropper from './AvatarCropper.vue'

const emit = defineEmits(['back'])

const userStore = useUserStore()
const profileStore = useProfileStore()
const notificationStore = useNotificationStore()

const displayName = computed(() => profileStore.profile?.displayName || userStore.user?.displayName || 'Musician')
const email = computed(() => profileStore.profile?.email || userStore.user?.email || '')
const currentTheme = computed(() => profileStore.profile?.theme || 'light')
const avatarUrl = computed(() => profileStore.profile?.avatarUrl)

const nicknameInput = ref(profileStore.profile?.nickname || '')
const nicknameSaving = ref(false)
let nicknameTimeout: ReturnType<typeof setTimeout> | null = null

watch(() => profileStore.profile?.nickname, (val) => {
  nicknameInput.value = val || ''
})

function onNicknameInput() {
  if (nicknameTimeout) clearTimeout(nicknameTimeout)
  nicknameSaving.value = true
  nicknameTimeout = setTimeout(async () => {
    await profileStore.updateProfile({ nickname: nicknameInput.value.trim() || '' })
    nicknameSaving.value = false
  }, 800)
}

// Avatar
const fileInput = ref<HTMLInputElement | null>(null)
const showCropDialog = ref(false)
const cropImageUrl = ref('')
const avatarUploading = ref(false)

const handleAvatarSelect = (e: Event) => {
  const file = (e.target as HTMLInputElement).files?.[0]
  if (!file) return
  cropImageUrl.value = URL.createObjectURL(file)
  showCropDialog.value = true
  // Reset input so same file can be re-selected
  if (fileInput.value) fileInput.value.value = ''
}

const handleCrop = async (blob: Blob) => {
  avatarUploading.value = true
  try {
    await profileStore.uploadAvatar(blob)
    showCropDialog.value = false
  } catch (error) {
    console.error('Failed to upload avatar:', error)
  } finally {
    avatarUploading.value = false
    URL.revokeObjectURL(cropImageUrl.value)
  }
}

const handleCancelCrop = () => {
  showCropDialog.value = false
  URL.revokeObjectURL(cropImageUrl.value)
}

const handleRemoveAvatar = async () => {
  try {
    await profileStore.deleteAvatar()
  } catch (error) {
    console.error('Failed to remove avatar:', error)
  }
}

async function setTheme(theme: string) {
  if (theme === currentTheme.value) return
  await profileStore.updateProfile({ theme })
}

const currentAiModel = computed(() => profileStore.profile?.aiModel || 'Standard')

async function setAiModel(aiModel: string) {
  if (aiModel === currentAiModel.value) return
  await profileStore.updateProfile({ aiModel })
}

const aiThinking = computed(() => profileStore.profile?.aiThinking ?? true)

async function toggleAiThinking() {
  await profileStore.updateProfile({ aiThinking: !aiThinking.value })
}

// Audio input devices for recording
const audioInputDevices = ref<{ deviceId: string; label: string }[]>([])
const micLabelsVisible = ref(false)

const selectedAudioDeviceId = computed({
  get: () => profileStore.profile?.audioInputDeviceId ?? '',
  set: () => {} // setter via onAudioDeviceChange below
})
const currentAudioChannel = computed(() => profileStore.profile?.audioInputChannel || 'stereo')

async function refreshAudioDevices() {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const inputs = devices.filter(d => d.kind === 'audioinput')
    // If labels are non-empty, mic permission has been granted at some point.
    micLabelsVisible.value = inputs.some(d => d.label !== '')
    audioInputDevices.value = inputs.map(d => ({
      deviceId: d.deviceId,
      label: d.label || `Microphone (${d.deviceId.slice(0, 8)}…)`
    }))
  } catch (e) {
    console.error('Failed to enumerate audio inputs:', e)
  }
}

async function requestMicPermissionForLabels() {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    stream.getTracks().forEach(t => t.stop())
    await refreshAudioDevices()
  } catch (e) {
    console.error('Mic permission denied:', e)
  }
}

async function onAudioDeviceChange(e: Event) {
  const id = (e.target as HTMLSelectElement).value
  const device = audioInputDevices.value.find(d => d.deviceId === id)
  await profileStore.updateProfile({
    audioInputDeviceId: id || '',
    audioInputDeviceLabel: device?.label || '',
  })
}

async function setAudioChannel(channel: string) {
  if (channel === currentAudioChannel.value) return
  await profileStore.updateProfile({ audioInputChannel: channel })
}

// Notifications
const notificationsEnabled = computed(() => notificationStore.isEnabled)
const notificationsLoading = computed(() => notificationStore.loading)
const notificationsSupported = computed(() => notificationStore.isSupported)
const notificationsDenied = computed(() => notificationStore.permission === 'denied')

async function toggleNotifications() {
  if (notificationsLoading.value) return
  if (notificationStore.isEnabled) {
    await notificationStore.disable()
  } else {
    await notificationStore.enable()
  }
}

onMounted(() => {
  refreshAudioDevices()
  // Refresh when devices are plugged/unplugged while the page is open.
  navigator.mediaDevices.addEventListener?.('devicechange', refreshAudioDevices)
})
</script>

<template>
  <div class="p-4">
    <!-- Back button -->
    <button class="flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-800 mb-6" @click="emit('back')">
      <i class="pi pi-arrow-left text-xs"></i>
      Back
    </button>

    <div class="rd-card p-6">
      <h2 class="text-lg font-bold text-slate-900 mb-6">Profile</h2>

      <!-- User info -->
      <div class="flex items-center gap-4 mb-8">
        <div class="relative group">
          <img v-if="avatarUrl" :src="avatarUrl" class="w-16 h-16 rounded-full object-cover border-2 border-slate-200" />
          <Avatar v-else :label="displayName.charAt(0)" size="xlarge" shape="circle" severity="primary" />
          <button
            class="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            @click="fileInput?.click()"
          >
            <i class="pi pi-camera text-white text-lg"></i>
          </button>
          <input ref="fileInput" type="file" accept="image/jpeg,image/png,image/webp" class="hidden" @change="handleAvatarSelect" />
        </div>
        <div>
          <p class="text-lg font-semibold text-slate-900">{{ displayName }}</p>
          <p class="text-sm text-slate-500">{{ email }}</p>
          <button v-if="avatarUrl" class="text-xs text-red-500 hover:text-red-700 mt-0.5" @click="handleRemoveAvatar">Remove avatar</button>
        </div>
      </div>

      <!-- Avatar Crop Dialog -->
      <Dialog v-model:visible="showCropDialog" modal header="Crop Avatar" :style="{ width: '24rem' }" :closable="!avatarUploading">
        <div class="flex flex-col items-center">
          <AvatarCropper v-if="showCropDialog" :imageUrl="cropImageUrl" @crop="handleCrop" @cancel="handleCancelCrop" />
          <div v-if="avatarUploading" class="mt-2 text-sm text-slate-500">
            <i class="pi pi-spin pi-spinner mr-1"></i> Uploading...
          </div>
        </div>
      </Dialog>

      <!-- Nickname -->
      <div class="rd-section">
        <p class="rd-label mb-3">Nickname</p>
        <p class="text-xs text-slate-500 mb-2">Set a nickname to be shown instead of your display name in bands and chats.</p>
        <div class="flex items-center gap-2">
          <input
            v-model="nicknameInput"
            type="text"
            class="rd-input w-full max-w-xs"
            placeholder="Enter a nickname..."
            maxlength="30"
            @input="onNicknameInput"
          />
          <i v-if="nicknameSaving" class="pi pi-spin pi-spinner text-slate-400 text-sm"></i>
        </div>
      </div>

      <!-- Notifications -->
      <div class="rd-section">
        <p class="rd-label mb-3">Notifications</p>
        <p class="text-xs text-slate-500 mb-3">Get push notifications for new chat messages and when someone joins your band.</p>

        <div v-if="!notificationsSupported" class="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <i class="pi pi-info-circle mr-1"></i>
          Push notifications are not supported in this browser. On iPhone or iPad, install Riff Draft to your home screen first.
        </div>

        <div v-else-if="notificationsDenied" class="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <i class="pi pi-exclamation-triangle mr-1"></i>
          Notifications are blocked. To enable them, click the lock icon in your browser's address bar, allow notifications, then reload the page.
        </div>

        <div v-else class="flex items-center gap-3">
          <ToggleSwitch
            :modelValue="notificationsEnabled"
            :disabled="notificationsLoading"
            @update:modelValue="toggleNotifications"
          />
          <span class="text-sm text-slate-700">
            {{ notificationsEnabled ? 'Enabled' : 'Disabled' }}
          </span>
          <i v-if="notificationsLoading" class="pi pi-spin pi-spinner text-slate-400 text-sm"></i>
        </div>
      </div>

      <!-- Recording -->
      <div class="rd-section">
        <p class="rd-label mb-3">Audio System</p>

        <div v-if="!micLabelsVisible" class="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
          <i class="pi pi-info-circle mr-1"></i>
          Grant microphone access to see device names.
          <button class="text-teal-600 hover:text-teal-800 ml-2 underline" @click="requestMicPermissionForLabels">Grant access</button>
        </div>

        <div class="mb-4">
          <p class="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Input Device</p>
          <select
            class="rd-input w-full max-w-md"
            :value="selectedAudioDeviceId"
            @change="onAudioDeviceChange"
          >
            <option value="">System default</option>
            <option v-for="d in audioInputDevices" :key="d.deviceId" :value="d.deviceId">{{ d.label }}</option>
          </select>
        </div>

        <div>
          <p class="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Channel</p>
          <div class="flex gap-3">
            <button
              v-for="ch in [{ id: 'stereo', label: 'Stereo' }, { id: 'left', label: 'Left' }, { id: 'right', label: 'Right' }]"
              :key="ch.id"
              class="px-4 py-2.5 rounded-xl border-2 transition-all text-sm font-medium"
              :class="currentAudioChannel === ch.id
                ? 'border-teal-500 bg-teal-50 text-teal-700'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'"
              @click="setAudioChannel(ch.id)"
            >
              {{ ch.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Theme -->
      <div class="rd-section">
        <p class="rd-label mb-3">Theme</p>
        <div class="flex gap-3">
          <button
            class="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all"
            :class="currentTheme === 'light'
              ? 'border-teal-500 bg-teal-50 text-teal-700'
              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'"
            @click="setTheme('light')"
          >
            <i class="pi pi-sun text-lg"></i>
            <span class="text-sm font-medium">Light</span>
          </button>
          <button
            class="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all"
            :class="currentTheme === 'dark'
              ? 'border-teal-500 bg-teal-50 text-teal-700'
              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'"
            @click="setTheme('dark')"
          >
            <i class="pi pi-moon text-lg"></i>
            <span class="text-sm font-medium">Dark</span>
          </button>
        </div>
      </div>

      <!-- AI Model -->
      <div class="rd-section">
        <p class="rd-label mb-3">AI Model</p>
        <p class="text-xs text-slate-500 mb-3">Choose the model used to generate AI clips. Standard is faster and cheaper; Advanced uses a larger reasoning model for higher-quality results.</p>
        <div class="flex gap-3">
          <button
            class="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all"
            :class="currentAiModel === 'Standard'
              ? 'border-teal-500 bg-teal-50 text-teal-700'
              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'"
            @click="setAiModel('Standard')"
          >
            <i class="pi pi-bolt text-lg"></i>
            <span class="text-sm font-medium">Standard</span>
          </button>
          <button
            class="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all"
            :class="currentAiModel === 'Advanced'
              ? 'border-teal-500 bg-teal-50 text-teal-700'
              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'"
            @click="setAiModel('Advanced')"
          >
            <i class="pi pi-sparkles text-lg"></i>
            <span class="text-sm font-medium">Advanced</span>
          </button>
        </div>
        <div class="mt-4 pt-4 border-t border-slate-200">
          <p class="rd-label mb-3">Reasoning</p>
          <p class="text-xs text-slate-500 mb-3">When on, the model uses adaptive reasoning for higher-quality results. When off, reasoning is minimized for faster, cheaper responses.</p>
          <div class="flex items-center gap-3">
            <ToggleSwitch
              :modelValue="aiThinking"
              @update:modelValue="toggleAiThinking"
            />
            <span class="text-sm text-slate-700">
              {{ aiThinking ? 'Enabled' : 'Disabled' }}
            </span>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>
